import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useBillItems } from './useBillItems';
import { useIssueCreditNote } from './useIssueCreditNote';
import type { Bill, PaymentMode } from './types';

/**
 * ISS-08: fixes a wrong OPD/IP bill without ever editing or deleting the
 * original invoice — a credit note is its own numbered document
 * (CN/<FY>/<SEQ>), mirroring the pharmacy counter's own returns flow. Pharmacy
 * bills are rejected server-side and use that existing flow instead (see
 * backend/utils/billingCreditNotes.js).
 */
export default function IssueCreditNoteDialog({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const itemsQuery = useBillItems(bill.id);
  const items = itemsQuery.data ?? [];
  const creditableItems = items.filter((i) => i.quantity - i.returned_quantity > 0);

  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [refundMode, setRefundMode] = useState<PaymentMode>(bill.payment_mode);
  const [refundStatus, setRefundStatus] = useState<'Paid' | 'Pending'>('Paid');

  const issueCreditNote = useIssueCreditNote(bill.id);

  const selectedItems = useMemo(
    () =>
      creditableItems
        .map((item) => ({ item, qty: parseInt(quantities[item.id] || '0', 10) }))
        .filter(({ qty }) => Number.isInteger(qty) && qty > 0),
    [creditableItems, quantities]
  );
  const estimatedTotal = selectedItems.reduce((sum, { item, qty }) => {
    const unitNet = parseFloat(item.line_total) / item.quantity;
    const unitGst = parseFloat(item.gst_amount || '0') / item.quantity;
    return sum + (unitNet + unitGst) * qty;
  }, 0);

  const valid = selectedItems.length > 0 && reason.trim().length > 0;

  const submit = () => {
    if (!valid) return;
    issueCreditNote.mutate(
      {
        reason: reason.trim(),
        items: selectedItems.map(({ item, qty }) => ({ bill_item_id: item.id, quantity: qty })),
        refund_mode: refundMode,
        refund_status: refundStatus,
      },
      { onSuccess: () => onClose() }
    );
  };

  const error = issueCreditNote.error instanceof ApiError ? issueCreditNote.error.message : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Issue credit note</DialogTitle>
        </DialogHeader>

        <div className="text-[13px] text-ink-muted">
          Against {bill.bill_number} ({bill.bill_type}) — the original invoice is never edited; this creates a separate numbered
          reversal document.
        </div>

        {error && <div className="rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

        {itemsQuery.isLoading ? (
          <LoadingState />
        ) : creditableItems.length === 0 ? (
          <EmptyState label="Nothing left on this bill to credit" />
        ) : (
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase text-ink-muted">Lines to credit</div>
            {creditableItems.map((item) => {
              const remaining = item.quantity - item.returned_quantity;
              return (
                <div key={item.id} className="grid grid-cols-[1fr_90px] items-end gap-2 rounded-[var(--radius)] border border-line p-2.5">
                  <div>
                    <div className="text-[13px] font-medium">{item.description}</div>
                    <div className="text-[11px] text-ink-muted">
                      {remaining} of {item.quantity} still creditable · ₹{parseFloat(item.line_total).toFixed(2)} line total
                    </div>
                  </div>
                  <FormField label="Qty">
                    <Input
                      type="number"
                      min="0"
                      max={remaining}
                      placeholder="0"
                      value={quantities[item.id] ?? ''}
                      onChange={(e) => setQuantities((prev) => ({ ...prev, [item.id]: e.target.value }))}
                    />
                  </FormField>
                </div>
              );
            })}
          </div>
        )}

        <FormField label="Reason" required>
          <Textarea
            placeholder="e.g. Duplicate consultation fee charged by mistake"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="Refund mode">
            <NativeSelect value={refundMode} onChange={(e) => setRefundMode(e.target.value as PaymentMode)}>
              {(['Cash', 'UPI', 'Card'] as const).map((m) => (
                <option key={m}>{m}</option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField label="Refund status">
            <NativeSelect value={refundStatus} onChange={(e) => setRefundStatus(e.target.value as 'Paid' | 'Pending')}>
              <option value="Paid">Handed back now</option>
              <option value="Pending">Owed — settle later</option>
            </NativeSelect>
          </FormField>
        </div>

        {selectedItems.length > 0 && (
          <div className="rounded-[var(--radius)] bg-canvas p-3 text-[13px] font-semibold">
            Credit total: ₹{estimatedTotal.toFixed(2)}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!valid || issueCreditNote.isPending} loading={issueCreditNote.isPending} onClick={submit}>
            {issueCreditNote.isPending ? 'Issuing…' : 'Issue credit note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
